import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import './Home.css';
import DatePicker from 'react-datepicker';
import Dashboard from './Dashboard.js';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import "react-datepicker/dist/react-datepicker.css";
import { useCompany } from '../context/CompanyContext';

const Home = () => {

  const { selectedCompany } = useCompany();

  const getStartDate = () => {
    var today = new Date();
    return new Date(new Date().setMonth(today.getMonth() - 3));
  };
  
  const getEndDate = () => {
    var today = new Date();
    return new Date(new Date().setMonth(today.getMonth() + 1));;
  };

  const [topLevelData, setTopLevelData] = useState({
    'candidates_renew': 0,
    'schedule_to_date': 0,
    'total_job_open': 0,
  });
  const [jobStatsData, setJobStatsData] = useState({
    'overtime': [],
    'total_view': 0,
    'total_applied': 0
  });
  const [candidateBreakdownData, setCandidateBreakdownData] = useState([]);
  const [jobTypeBreakdownData, setJobTypeBreakdownData] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setLoading] = useState({
    "top": false,
    "chart": false,
    "job_stats": false,
    "updates": false,
    "job_type": false
  });

  const [jobs, setJobsData] = useState([]);
  // const [companyId, setCompanyId] = useState(null);
  const [startDate, setStartDate] = useState(getStartDate());
  const [endDate, setEndDate] = useState(getEndDate());
  const navigate = useNavigate();
  const [userData, setUserData] = useState({
      "name": "",
      "email": "",
      "company": ""
    });

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
    }

    const names = localStorage.getItem('names');
    const email = localStorage.getItem('email');

    setUserData({
        "name": names,
        "email": email,
        "company": ""
    });
    
    // In a real application, you would fetch user data from API here
    // For now, we're using the hardcoded data above
  }, [navigate]);

  const fetchTopLevel = async (companyId) => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Authentication token not found');
        setLoading({
          ...isLoading,
          top: true
        });
        return;
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/analytics/top-level/${companyId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTopLevelData(data);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to fetch JSON data');
      }
    } catch (error) {
      setError('Failed to fetch JSON data');
      console.error('Error fetching JSON data:', error);
    } finally {
      setLoading({
        ...isLoading,
        top: false
      });
    }
  };

  const fetchStatsOvertime = async (companyId) => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Authentication token not found');
        setLoading({
          ...isLoading,
          job_stats: true
        });
        return;
      }
      const params = new URLSearchParams({
        start: formatDateAPI(startDate),
        end: formatDateAPI(endDate),
      });
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/analytics/job-stats/${companyId}?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setJobStatsData(data);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to fetch JSON data');
      }
    } catch (error) {
      setError('Failed to fetch JSON data');
      console.error('Error fetching JSON data:', error);
    } finally {
      setLoading({
        ...isLoading,
        job_stats: false
      });
    }
  };

  const fetchCandidate = async (companyId) => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Authentication token not found');
        setLoading({
          ...isLoading,
          chart: true
        });
        return;
      }
      const params = new URLSearchParams({
        start: formatDateAPI(startDate),
        end: formatDateAPI(endDate),
      });
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/analytics/applicate-summary/${companyId}?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCandidateBreakdownData(data);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to fetch JSON data');
      }
    } catch (error) {
      setError('Failed to fetch JSON data');
      console.error('Error fetching JSON data:', error);
    } finally {
      setLoading({
        ...isLoading,
        chart: false
      });
    }
  };

  const fetchJobType = async (companyId) => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Authentication token not found');
        setLoading({
          ...isLoading,
          job_type: true
        });
        return;
      }
      const params = new URLSearchParams({
        start: formatDateAPI(startDate),
        end: formatDateAPI(endDate),
      });
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/analytics/job-type-summary/${companyId}?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setJobTypeBreakdownData(data);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to fetch JSON data');
      }
    } catch (error) {
      setError('Failed to fetch JSON data');
      console.error('Error fetching JSON data:', error);
    } finally {
      setLoading({
        ...isLoading,
        job_type: false
      });
    }
  };

  const fetchJobs = async (companyId) => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Authentication token not found');
        setLoading({
          ...isLoading,
          updates: true
        });
        return;
      }
      const params = new URLSearchParams({
        page: "1",
        size: "5",
      });
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/jobs/list-company-jobs/${companyId}?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setJobsData(data.items);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to fetch JSON data');
      }
    } catch (error) {
      setError('Failed to fetch JSON data');
      console.error('Error fetching JSON data:', error);
    } finally {
      setLoading({
        ...isLoading,
        updates: false
      });
    }
  };

  useEffect(() => {
    if (selectedCompany) {
      fetchStatsOvertime(selectedCompany.company_id);
      fetchCandidate(selectedCompany.company_id);
      fetchJobType(selectedCompany.company_id);
    }
  }, [startDate, endDate, selectedCompany]);

  useEffect(() => {
    if (selectedCompany) {
      fetchTopLevel(selectedCompany.company_id);
      fetchJobs(selectedCompany.company_id);
    }
  }, [selectedCompany]);

  const formatDateAPI = (date) => {
    return date.toISOString().substring(0, 10);
  }

  const formatDate = (date) => {
    if (date === null) {
      return "";
    }
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).replace(',', '');
  }

  return (
    <Dashboard page={"home"}>
      <div className="dashboard-content">
    <div className="welcome-section">
      <div>
        <h2>Hello, {userData.name.split(' ')[0]}</h2>
        <p>Here is your job listings statistic report from {formatDate(startDate)}, {formatDate(endDate)} .</p>
      </div>
      <div className="date-picker">
        {/* <span>{userData.dateRange}</span> */}
        {/* <button className="calendar-btn">📅</button> */}
        <DatePicker
              selectsRange
              startDate={startDate}
              endDate={endDate}
              onChange={dates => {
                const [start, end] = dates;
                setStartDate(start);
                setEndDate(end);
              }}
              calendarClassName="custom-calendar"
              className="custom-datepicker"
            />
      </div>
    </div>

    <div className="stats-cards">
      <div className="stat-card purple">
        <div>
          <h2>{topLevelData.candidates_renew}</h2>
          <p>New candidates to review</p>
        </div>
        <button className="card-action">→</button>
      </div>
      <div className="stat-card green">
        <div>
          <h2>{topLevelData.schedule_to_date}</h2>
          <p>Schedule for today</p>
        </div>
        <button className="card-action">→</button>
      </div>
      <div className="stat-card blue">
        <div>
          <h2>{0}</h2>
          <p>Messages received</p>
        </div>
        <button className="card-action">→</button>
      </div>
    </div>

    <div className="dashboard-grid">
      <div className="chart-section">
        <div className="section-header">
          <h3>Job statistics</h3>
          <p>Showing Jobstatistic {formatDate(startDate)}, {formatDate(endDate)}</p>
        </div>

        <div className="chart-tabs">
          <button className="chart-tab active">Overview</button>
          <button className="chart-tab">Jobs View</button>
          <button className="chart-tab">Jobs Applied</button>
        </div>

        <div className="recharts-wrapper" style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer>
                <BarChart data={jobStatsData.overtime}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="views" fill="#f59e0b" name="Job Views" />
                  <Bar dataKey="applications" fill="#4f46e5" name="Job Applied" />
                </BarChart>
              </ResponsiveContainer>
        </div>

        <div className="chart-metrics">
          <div className="metric">
            <div className="metric-header">
              <h4>Job Views</h4>
              <span className="metric-icon views">👁️</span>
            </div>
            <div className="metric-value">
              <h3>{jobStatsData.total_view.toLocaleString()}</h3>
              <span className="metric-change positive">6.4% ▲</span>
            </div>
            <p>This Week</p>
          </div>

          <div className="metric">
            <div className="metric-header">
              <h4>Job Applied</h4>
              <span className="metric-icon applied">📊</span>
            </div>
            <div className="metric-value">
              <h3>{jobStatsData.total_applied.toLocaleString()}</h3>
              <span className="metric-change negative">0.5% ▼</span>
            </div>
            <p>This Week</p>
          </div>
        </div>
      </div>

      <div className="stats-boxes">
        <div className="stat-box">
          <h3>Job Open</h3>
          <div className="box-content">
            <h2>{topLevelData.total_job_open}</h2>
            <p>Jobs Opened</p>
          </div>
        </div>

        <div className="stat-box">
          <h3>Applicants Summary</h3>
          <div className="box-content">
            <h2>{topLevelData.candidates_renew}</h2>
            <p>Applicants</p>
          </div>
          <div className="applicant-types">
          {candidateBreakdownData.map((item, index) => {

            const colors = ['purple', 'orange', 'green', 'red', 'blue'];
            const colorClass = colors[index % colors.length];

            return (
              <div className="type">
                <div className={`type-bar ${colorClass}`}></div>
                <div className="type-info">
                  <span>{item.job_type}</span>
                  <span>: {item.current_jobs}</span>
                </div>
              </div>
            );

          })}
          </div>
        </div>
      </div>
    </div>

    <div className="job-updates">
      <div className="section-header">
        <h3>Job Updates</h3>
        <a href="#" className="view-all">View All →</a>
      </div>

      <div className="job-listings">
        {jobs.map(job => (
          <div key={job.id} className="job-card">
            <div className="job-company-logo">{job.company_name.charAt(0)}</div>
            <div className="job-type">{job.job_type}</div>
            <h4 className="job-title">{job.title}</h4>
            <div className="job-location">
              {job.address.country} • {job.location_type}
            </div>
            {/* <div className="job-tags">
              {job.tags.map((tag, index) => (
                <span 
                  key={index} 
                  className={`job-tag ${tag.toLowerCase()}`}
                >
                  {tag}
                </span>
              ))}
            </div>
            <div className="job-applicants">
              {job.applicants} applied of {job.capacity} capacity
            </div> */}
          </div>
        ))}
      </div>
    </div>
  </div>
  </Dashboard>
  );
};

export default Home;